import { formatDateWithFormatter } from '@/utils/function'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowBack, ArrowForward, Typogaphy } from 'vnetwork-ui'

dayjs.extend(isoWeek)

interface WeekCalendarProps {
    selectedDate: Date
    setSelectedDate: (date: Date) => void
    weekStart: Dayjs
    onPrevWeek: () => void
    onNextWeek: () => void
    /** Whether to include Sunday in the week view. Default: false (Mon-Sat only) */
    includeSunday?: boolean
    /** Whether to disable future dates. Default: true */
    disableFuture?: boolean
    /** Whether to disable the previous week button */
    isPrevWeekDisabled?: boolean
    /** Optional function to check if a day has data/events */
    hasData?: (day: Date) => boolean
    /** Optional count to show on days with data */
    getDataCount?: (day: Date) => number
}

const WeekCalendar: React.FC<WeekCalendarProps> = ({
    selectedDate,
    setSelectedDate,
    weekStart,
    onPrevWeek,
    onNextWeek,
    includeSunday = false,
    disableFuture = true,
    isPrevWeekDisabled = false,
    hasData,
    getDataCount,
}) => {
    const { t } = useTranslation()
    const today = dayjs()

    // Generate days in week (Mon-Sat or Mon-Sun)
    const daysInWeek = useMemo(() => {
        const days: Date[] = []
        const dayCount = includeSunday ? 7 : 6
        for (let i = 0; i < dayCount; i++) {
            days.push(weekStart.add(i, 'day').toDate())
        }
        return days
    }, [weekStart, includeSunday])

    const dayLabels = includeSunday
        ? ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
        : ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    // Check if next week button should be disabled (max = current day's week)
    const isNextWeekDisabled = useMemo(() => {
        if (!disableFuture) return false
        return weekStart.isSame(today, 'isoWeek')
    }, [weekStart, today, disableFuture])

    return (
        <div className="mb-3">
            {/* Month Header */}
            <div className="flex justify-center items-center mb-2">
                <h4 className="font-semibold text-primary text-sm">
                    {t('month')}{' '}
                    {formatDateWithFormatter(
                        selectedDate.toLocaleString(),
                        'MM, YYYY'
                    )}
                </h4>
            </div>

            {/* Week Grid */}
            <div className="flex justify-center items-center">
                <div
                    className={`flex items-center justify-center min-w-[30px] h-[30px] rounded-full
                        transition-colors
                        ${
                            isPrevWeekDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'hover:bg-secondary-20 cursor-pointer'
                        }
                    `}
                    onClick={() => !isPrevWeekDisabled && onPrevWeek()}
                >
                    <ArrowBack
                        className={`w-3.5 h-3.5 ${isPrevWeekDisabled ? 'text-gray-300' : 'text-primary'}`}
                    />
                </div>

                <div className="w-full">
                    {/* Day Labels */}
                    <div
                        className={`grid gap-0.5 text-center text-xs w-full ${includeSunday ? 'grid-cols-7' : 'grid-cols-6'}`}
                    >
                        {dayLabels.map((day) => (
                            <div
                                key={day}
                                className="text-gray-400 p-0.5 font-medium"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Day Numbers */}
                    <div
                        className={`grid gap-0.5 text-center text-sm w-full ${includeSunday ? 'grid-cols-7' : 'grid-cols-6'}`}
                    >
                        {daysInWeek.map((day: Date) => {
                            const dayDayjs = dayjs(day)
                            const selectedDateDayjs = dayjs(selectedDate)

                            const isSelected = dayDayjs.isSame(
                                selectedDateDayjs,
                                'day'
                            )
                            const isTodayDate = dayDayjs.isSame(today, 'day')
                            const isFuture =
                                disableFuture && dayDayjs.isAfter(today, 'day')
                            const dayHasData = hasData?.(day) ?? false
                            const dataCount = getDataCount?.(day) ?? 0

                            return (
                                <div
                                    key={day.toString()}
                                    className="col-span-1 flex items-center justify-center"
                                >
                                    <div
                                        onClick={() =>
                                            !isFuture && setSelectedDate(day)
                                        }
                                        className={`
                                            rounded-full w-[32px] h-[32px] transition-colors
                                            flex items-center justify-center relative
                                            ${
                                                isFuture
                                                    ? 'text-gray-300 cursor-not-allowed'
                                                    : 'cursor-pointer'
                                            }
                                            ${isTodayDate && !isSelected ? 'text-primary font-bold' : ''}
                                            ${isSelected ? 'bg-primary text-white' : ''}
                                        `}
                                    >
                                        <Typogaphy className="text-sm font-medium">
                                            {formatDateWithFormatter(
                                                day.toLocaleString(),
                                                'D'
                                            )}
                                        </Typogaphy>
                                        {/* Data indicator dot */}
                                        {dayHasData &&
                                            !isSelected &&
                                            !isFuture && (
                                                <div className="w-1.5 h-1.5 bg-red-500 rounded-full absolute -top-0.5 -right-0.5"></div>
                                            )}
                                        {/* Data count badge */}
                                        {dataCount > 0 && isSelected && (
                                            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                                {dataCount > 9
                                                    ? '9+'
                                                    : dataCount}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div
                    className={`flex items-center justify-center min-w-[30px] h-[30px] rounded-full
                        transition-colors
                        ${
                            isNextWeekDisabled
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'hover:bg-secondary-20 cursor-pointer'
                        }
                    `}
                    onClick={() => !isNextWeekDisabled && onNextWeek()}
                >
                    <ArrowForward
                        className={`w-3.5 h-3.5 ${isNextWeekDisabled ? 'text-gray-300' : 'text-primary'}`}
                    />
                </div>
            </div>
        </div>
    )
}

export default WeekCalendar
